import { useTranslation } from "react-i18next";

import { GlobeCrossedOut } from "@/assets/icons/ds";
import { ButtonLink } from "@/components/Interface/Button/Button";
import { ProfileCard } from "@/components/Layout/BaseLayouts/ProfileCard/ProfileCard";
import { CountrylistDialog } from "@/components/Portal/DeviceManagement/Roaming/AddRoamingOption/CountryList/CountryList";
import { RoamingInfo, SimCardDetail } from "@/services/model";

import { TravelPackageAccordionItem } from "./TravelPackageAccordionItem";

interface Props {
  /** All non-`na` roaming options for the sim (used for zone-first lookup). */
  roamingOptions: RoamingInfo[];
  /**
   * Travel packages to list — including surplus `includedInService` packages
   * that were demoted by {@link partitionRoamingOptionsForDisplay}.
   */
  travelPackages: RoamingInfo[];
  hasMobileAboRoaming: boolean;
  simDetail: SimCardDetail;
  bookingHref: string;
  disabled: boolean;
}

/** The "Gebuchte Pakete" card: booked travel roaming packages, or the empty state. */
export const BookedPackagesCard = ({
  roamingOptions,
  travelPackages,
  hasMobileAboRoaming,
  simDetail,
  bookingHref,
  disabled,
}: Props) => {
  const { t } = useTranslation("portal");

  return (
    <ProfileCard
      dataTestid="booked-packages-card"
      maxWidth="none"
      stickyHeader={false}
      title={t("cockpit.managementTile.roaming.card.bookedPackagesTitle")}
      actions={
        <ButtonLink
          data-testid="add-roaming-option-button"
          to={bookingHref}
          disabled={disabled}
          replace
          variant="primary"
          size="sm"
        >
          {t("cockpit.managementTile.roaming.card.selectPackage")}
        </ButtonLink>
      }
    >
      {travelPackages.length === 0 ? (
        <div className="flex w-full items-center gap-3 rounded-md bg-surface-quaternary p-4">
          <GlobeCrossedOut className="size-6 shrink-0 text-foreground-primary" />
          <p className="flex-1 text-center text-body-base font-semibold leading-none text-foreground-primary">
            {t("cockpit.managementTile.roaming.card.noActivePackage")}
          </p>
        </div>
      ) : (
        <>
          <p className="text-body-xs text-foreground-primary">
            {t("cockpit.managementTile.roaming.inAdditionToSubscription")}{" "}
            <CountrylistDialog />
          </p>

          {travelPackages.map((currentOption, currentIndex) => {
            // Prefer object identity (travel packages keep the original
            // references). Surplus included packages are clones with
            // includedInService cleared, so fall back to optionId /
            // roamingProduct against the first option still flagged included.
            const firstOptionInZone = roamingOptions.find(
              (option) => option.usageZone === currentOption.usageZone,
            );
            const isFirstOfUsageZone =
              firstOptionInZone === currentOption ||
              (!!firstOptionInZone?.includedInService &&
                !currentOption.includedInService &&
                firstOptionInZone.optionId === currentOption.optionId &&
                (firstOptionInZone.roamingProduct ?? "") ===
                  (currentOption.roamingProduct ?? ""));

            return (
              <TravelPackageAccordionItem
                key={`${currentOption.simId}-${currentOption.optionId}-${currentOption.roamingProduct ?? ""}-${currentIndex}`}
                defaultOpen={currentIndex === 0}
                roamingInfo={currentOption}
                simDetail={simDetail}
                index={hasMobileAboRoaming ? currentIndex + 1 : currentIndex}
                isFirstOfUsageZone={isFirstOfUsageZone}
              />
            );
          })}
        </>
      )}
    </ProfileCard>
  );
};
